"use client";

import { useCallback, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import type { CanvasVariant, VariantAxis } from "src/components/canvas/variant-resolver";
import { formatMoney } from "src/components/canvas/variant-price";
import { useShopCurrencyValue } from "src/hooks/useShopMarket";
import { offeredSizes } from "./artwork-sizing";
import {
  entryVariantId,
  materialOffers,
  sizeRange,
  variantAxesOf,
  type SizeCm,
} from "./artwork-purchase";
import { localized, type Artwork } from "./artwork-types";
import type { ArtworkCardLinks } from "./ArtworkCard";

type CardOfferProps = Pick<ArtworkCardLinks, "offers" | "sizeLabel" | "roomSize">;

/** "30 × 40", width first, as the work hangs. */
export const formatSize = (size: SizeCm) => `${size.width} × ${size.height}`;

/**
 * The material name the shop uses for an axis value: the canvas editor's own
 * label, or the definition's when the editor has none.
 */
export function useMaterialLabel(axes: VariantAxis[]) {
  const tEditor = useTranslations("editor");
  const locale = useLocale();
  return useCallback(
    (material: string) => {
      const key = `canvasMaterial_${material}`;
      if (tEditor.has(key)) return tEditor(key);
      const def = axes.find((a) => a.key === "material")?.values.find((v) => v.value === material);
      return def ? localized(def.label, locale) : material;
    },
    [tEditor, axes, locale],
  );
}

/**
 * What each card says about price and size: one "from" line per material
 * this work can be printed on, the range of its sizes, and the size its
 * room preview hangs at. Worked out once per work and list, not per render.
 *
 * `null` variants (still loading) give no lines rather than a wrong number.
 */
export function useCardOffers(
  artworks: Artwork[],
  variants: CanvasVariant[] | null | undefined,
): (artwork: Artwork) => CardOfferProps {
  const t = useTranslations("art");
  const locale = useLocale();
  const currency = useShopCurrencyValue();
  const axes = useMemo(() => variantAxesOf(variants), [variants]);
  const materialLabel = useMaterialLabel(axes);

  const byId = useMemo(() => {
    const map = new Map<string, CardOfferProps>();
    if (!variants?.length) return map;
    for (const artwork of artworks) {
      const printable = offeredSizes(artwork, variants);
      const offers = materialOffers(printable, currency).map((offer) => ({
        label: materialLabel(offer.material),
        price: t("fromPrice", {
          price: formatMoney(offer.price.amount, offer.price.currency, locale),
        }),
      }));
      const range = sizeRange(printable);
      const sizeLabel = !range
        ? undefined
        : formatSize(range.from) === formatSize(range.to)
          ? t("sizeSingle", { size: formatSize(range.from) })
          : t("sizeRange", { from: formatSize(range.from), to: formatSize(range.to) });
      const entryId = entryVariantId(printable, axes);
      const entry = printable.find((p) => p.variant.id === entryId);
      map.set(artwork.id, {
        offers,
        sizeLabel,
        roomSize: entry ? { widthMm: entry.widthMm, heightMm: entry.heightMm } : null,
      });
    }
    return map;
  }, [artworks, variants, currency, axes, materialLabel, t, locale]);

  return useCallback(
    (artwork: Artwork) => byId.get(artwork.id) ?? { offers: null, sizeLabel: undefined, roomSize: null },
    [byId],
  );
}
